import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { toast } from 'react-toastify';

interface Vendor {
  _id: string;
  name: string;
  description?: string;
  website?: string;
  status: 'active' | 'inactive';
}

interface NewVendor {
  name: string;
  description?: string;
  website?: string;
  status: 'active' | 'inactive';
}

const VendorsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);

  const [newVendor, setNewVendor] = useState<NewVendor>({
    name: '',
    description: '',
    website: '',
    status: 'active'
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/vendors');
      setVendors(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch vendors');
      setLoading(false);
      toast.error(t('vendors.messages.fetchError'));
    }
  };

  const resetForm = () => {
    setNewVendor({
      name: '',
      description: '',
      website: '',
      status: 'active'
    });
    setEditingVendor(null);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingVendor) {
        await api.put(`/vendors/${editingVendor._id}`, newVendor);
        toast.success(t('vendors.messages.updateSuccess'));
      } else {
        await api.post('/vendors', newVendor);
        toast.success(t('vendors.messages.createSuccess'));
      }
      fetchVendors();
      resetForm();
    } catch (err: any) {
      console.error('Submit error:', err.response?.data || err);
      toast.error(t('vendors.messages.saveError'));
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('vendors.deleteConfirmation'))) {
      try {
        await api.delete(`/vendors/${id}`);
        toast.success(t('vendors.messages.deleteSuccess'));
        fetchVendors();
      } catch (err) {
        toast.error(t('vendors.messages.deleteError'));
      }
    }
  };

  const handleEdit = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setNewVendor({
      name: vendor.name,
      description: vendor.description || '',
      website: vendor.website || '',
      status: vendor.status
    });
    setShowForm(true);
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && vendors.length === 0) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{t('vendors.title')}</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          {showForm ? t('common.cancel') : t('vendors.addVendor')}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('vendors.modal.name')}
              </label>
              <input
                type="text"
                value={newVendor.name}
                onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('vendors.modal.website')}
              </label>
              <input
                type="url"
                value={newVendor.website}
                onChange={(e) => setNewVendor({ ...newVendor, website: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('vendors.modal.description')}
              </label>
              <textarea
                value={newVendor.description}
                onChange={(e) => setNewVendor({ ...newVendor, description: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('vendors.modal.status')}
              </label>
              <select
                value={newVendor.status}
                onChange={(e) => setNewVendor({ ...newVendor, status: e.target.value as 'active' | 'inactive' })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="active">{t('vendors.modal.statusActive')}</option>
                <option value="inactive">{t('vendors.modal.statusInactive')}</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <button
              type="submit"
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mr-2"
            >
              {editingVendor ? t('vendors.modal.saveChanges') : t('vendors.modal.add')}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr>
              <th className="border px-4 py-2">{t('vendors.table.name')}</th>
              <th className="border px-4 py-2">{t('vendors.table.website')}</th>
              <th className="border px-4 py-2">{t('vendors.modal.description')}</th>
              <th className="border px-4 py-2">{t('vendors.table.status')}</th>
              <th className="border px-4 py-2">{t('vendors.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((vendor) => (
              <tr key={vendor._id}>
                <td className="border px-4 py-2">{vendor.name}</td>
                <td className="border px-4 py-2">
                  {vendor.website && (
                    <a
                      href={vendor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {vendor.website}
                    </a>
                  )}
                </td>
                <td className="border px-4 py-2">{vendor.description}</td>
                <td className="border px-4 py-2">
                  <span className={`px-2 py-1 rounded ${getStatusColor(vendor.status)}`}>
                    {vendor.status}
                  </span>
                </td>
                <td className="border px-4 py-2">
                  <button
                    onClick={() => handleEdit(vendor)}
                    className="text-blue-600 hover:text-blue-800 mr-2"
                  >
                    <PencilIcon className="h-5 w-5 inline" />
                  </button>
                  <button
                    onClick={() => handleDelete(vendor._id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="h-5 w-5 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VendorsManagement; 